import type { ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import { EditBar } from './EditBar';
import { SaveFeedback } from './SaveFeedback';
import { UnsavedChangesModal } from './UnsavedChangesModal';
import { useEditSession } from './useEditSession';

export interface SettingsSectionProps<T extends object> {
  /** Eindeutig je Abschnitt, z. B. `mandant:impressum` */
  id: string;
  title: string;
  description?: string;
  /** Geladene Werte; null, solange geladen wird */
  baseline: T | null;
  canEdit?: boolean;
  validate?: (value: T) => string[];
  /** Bekommt nur die geänderten Schlüssel in `changed` — nur diese schreiben */
  onSave: (value: T, changed: Array<keyof T>) => Promise<T | void>;
  renderRead: (value: T) => ReactNode;
  renderEdit: (value: T, setField: <K extends keyof T>(key: K, v: T[K]) => void) => ReactNode;
  testAnchor?: string;
}

/**
 * Abschnitt einer Einstellungsseite (Entscheid E10): zeigt zuerst an, „Bearbeiten" öffnet,
 * „Speichern" und „Abbrechen" führen zurück in die Anzeige. Nichts speichert still.
 */
export function SettingsSection<T extends object>(props: SettingsSectionProps<T>) {
  const { id, title, description, baseline, canEdit = true, testAnchor } = props;
  const session = useEditSession<T>({
    id: `settings:${id}`,
    label: title,
    baseline,
    canEdit,
    validate: props.validate,
    onSave: props.onSave,
  });
  const value = session.value;

  return (
    <section className="bg-slate-900 border border-slate-800 rounded-xl" data-test-anchor={testAnchor ?? `settings-${id}`} data-mode={session.mode}>
      <div className="flex flex-wrap items-start gap-2 p-4 border-b border-slate-800">
        <div className="min-w-0 flex-1">
          <h2 className="font-semibold text-slate-100">{title}</h2>
          {description && <p className="text-sm text-slate-400 mt-0.5">{description}</p>}
        </div>
        <EditBar
          editing={session.editing}
          dirty={session.dirty}
          saving={session.saving}
          canEdit={canEdit && value != null}
          onEdit={session.startEdit}
          onSave={() => { void session.save(); }}
          onCancel={session.cancel}
          changeCount={session.changedKeys.length}
        />
      </div>
      <div className="p-4">
        {value == null
          ? <div className="flex items-center justify-center py-6 text-slate-400"><Loader2 size={18} className="animate-spin" /></div>
          : session.editing ? props.renderEdit(value, session.setField) : props.renderRead(value)}
      </div>
      <UnsavedChangesModal {...session.unsavedModalProps} />
      <SaveFeedback label={title} error={session.error} clearError={session.clearError} issues={session.issues} clearIssues={session.clearIssues} />
    </section>
  );
}
